import { z } from 'zod/v4';
import { UNIT_VALUES } from '../../../units/contracts/index.js';

export const ORDER_ACTION_VALUES = ['read', 'edit', 'delete'] as const;
export type OrderAction = (typeof ORDER_ACTION_VALUES)[number];

export const ORDER_LINE_UNIT_VALUES = UNIT_VALUES;

export type OrderLineUnit = (typeof ORDER_LINE_UNIT_VALUES)[number];

export const ORDER_LINE_SECONDARY_UNIT = 'KILOGRAM' as const;

export const StylePropertyRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type StylePropertyRef = z.infer<typeof StylePropertyRefSchema>;

/** The style's CSV property columns resolved to their `style_properties` names,
 *  plus the style type. */
export const StylePropertiesSchema = z.object({
  seasons: z.array(StylePropertyRefSchema),
  brands: z.array(StylePropertyRefSchema),
  customProperties: z.array(StylePropertyRefSchema),
  collections: z.array(StylePropertyRefSchema),
  styleTypes: z.array(StylePropertyRefSchema),
});

export type StyleProperties = z.infer<typeof StylePropertiesSchema>;

export const OrderLineResourceSchema = z
  .object({
    id: z.string(),
    orderId: z.string(),
    externalId: z.string().nullable(),
    orderLineNumber: z.string().nullable(),
    lineNo: z.number().int().nullable(),
    styleId: z.string(),
    quantity: z.number(),
    unit: z.string(),
    secondaryQuantity: z.number().nullable(),
    secondaryUnit: z.string().nullable(),
    createdAt: z.string().nullable(),
    createdByUserId: z.string().nullable(),
    createdByUserName: z.string().nullable(),
    createdByCompanyId: z.string().nullable(),
    createdByCompanyName: z.string().nullable(),
    createdByImpersonateId: z.string().nullable(),
    createdByImpersonateName: z.string().nullable(),
    createdByApiKeyId: z.string().nullable(),
    createdByApiKeyLabel: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedByUserId: z.string().nullable(),
    updatedByUserName: z.string().nullable(),
    updatedByCompanyId: z.string().nullable(),
    updatedByCompanyName: z.string().nullable(),
    updatedByImpersonateId: z.string().nullable(),
    updatedByImpersonateName: z.string().nullable(),
    updatedByApiKeyId: z.string().nullable(),
    updatedByApiKeyLabel: z.string().nullable(),
    tracingReflectionOrderId: z.string().optional().nullable(),
    referencedTracingOrderId: z.string().optional().nullable(),
    tracingOrderRequestedAt: z.string().optional().nullable(),
    cmmOrderLineId: z.string().optional().nullable(),
    cmmRequestedAt: z.string().optional().nullable(),
    /** Status of the Tracing request this line was sent to or references. */
    tracingRequestStatus: z.string().optional().nullable(),
    styleProperties: StylePropertiesSchema.optional(),
    permissions: z.array(z.enum(ORDER_ACTION_VALUES)).optional(),
    order: z
      .object({
        id: z.string(),
        orderNumber: z.string(),
        orderType: z.string().nullable(),
        buyerCompanyId: z.string().nullable(),
        supplierCompanyId: z.string().nullable(),
        orderDate: z.string().nullable(),
        updatedAt: z.string().nullable(),
        updatedByUserName: z.string().nullable(),
        tracingStatus: z.string().nullable(),
        tags: z.array(z.string()),
        source: z.string().nullable(),
        buyerFacilityProcess: z.string().nullable(),
        supplierFacilityProcess: z.string().nullable(),
        isArchived: z.boolean(),
        tracingAttachmentTemplateId: z.string().nullable(),
      })
      .nullable(),
  })
  .strict();

export type OrderLineResource = z.infer<typeof OrderLineResourceSchema>;

/**
 * `quantity`/`secondary_quantity` are `numeric(19,2)` columns (max 17 integer
 * digits). The bounds below keep over-range/negative/NaN input from overflowing
 * the column (a Postgres `22003`/`22001`-class 500) and reject business-invalid
 * quantities. `line_no` is server-assigned, so it is never accepted as input.
 */
const NUMERIC_19_2_MAX = 1e17; // exclusive upper bound for 17 integer digits

export const ORDER_EXTERNAL_ID_MAX_LENGTH = 255;

const ExternalIdInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(ORDER_EXTERNAL_ID_MAX_LENGTH);

export const CreateOrderLineInputSchema = z
  .object({
    externalId: ExternalIdInputSchema.optional(),
    orderLineNumber: z.string().min(1).max(255).optional(),
    styleId: z.string().min(1).max(255),
    quantity: z.number().finite().positive().lt(NUMERIC_19_2_MAX),
    unit: z.enum(ORDER_LINE_UNIT_VALUES),
    secondaryQuantity: z
      .number()
      .finite()
      .positive()
      .lt(NUMERIC_19_2_MAX)
      .optional(),
    secondaryUnit: z.literal(ORDER_LINE_SECONDARY_UNIT).optional(),
  })
  .strict();

export type CreateOrderLineInput = z.infer<typeof CreateOrderLineInputSchema>;

export const UpdateOrderLineInputSchema =
  CreateOrderLineInputSchema.partial().extend({
    externalId: ExternalIdInputSchema.nullable().optional(),
    secondaryQuantity: z
      .number()
      .finite()
      .positive()
      .lt(NUMERIC_19_2_MAX)
      .nullable()
      .optional(),
    secondaryUnit: z.literal(ORDER_LINE_SECONDARY_UNIT).nullable().optional(),
  });

export type UpdateOrderLineInput = z.infer<typeof UpdateOrderLineInputSchema>;

function requireNewLineFields(
  val: { id?: string; styleId?: string; quantity?: number; unit?: string },
  ctx: z.RefinementCtx,
): void {
  if (val.id !== undefined) return;
  for (const field of ['styleId', 'quantity', 'unit'] as const) {
    if (val[field] === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: `${field} is required when creating a new order line (no id)`,
      });
    }
  }
}

const UpsertOrderLineObjectSchema = UpdateOrderLineInputSchema.extend({
  id: z.string().min(1).max(ORDER_EXTERNAL_ID_MAX_LENGTH).optional(),
});

export const UpsertOrderLineInputSchema =
  UpsertOrderLineObjectSchema.superRefine(requireNewLineFields);

export type UpsertOrderLineInput = z.infer<typeof UpsertOrderLineInputSchema>;

/** Dashboard line inputs: `externalId` is written through the Public API only. */
export const DashboardCreateOrderLineInputSchema =
  CreateOrderLineInputSchema.omit({ externalId: true });

export const DashboardUpsertOrderLineInputSchema =
  UpsertOrderLineObjectSchema.omit({ externalId: true }).superRefine(
    requireNewLineFields,
  );
